import React, { useEffect } from 'react'
import { InferType } from 'yup'
import { Controller, useForm } from 'react-hook-form'
import { captureException } from '@sentry/react'
import { useNavigate } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutation, useQuery } from '@tanstack/react-query'
import { societySchema } from '../../../forms/schemas/society'
import { SocietyTypes } from '../../../types/society-type'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { ToastType } from '../../common/Toast/toast-type'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { FormControl } from '../../common/FormControl'
import { useFormErrorMessage } from '../../../hooks/useFormErrorMessage'
import { TextInput } from '../../common/TextInput'
import { Button, ButtonVariant } from '../../common/Button'
import { reverseUrl } from '../../../navigation/reverseUrl'
import { AlreadyExistingSocietyModal } from './AlreadyExistingSocietyModal'
import { HorizontalFormSection } from '../../common/HorizontalFormSection'
import { formatOrganisationNumber } from '../../../helpers/organisation_number'
import { OrganisationNumberTextInput } from '../../common/OrganisationNumberTextInput'
import { IUserModel } from '../../../interfaces/models/users.interfaces'
import {
  activateSociety,
  createSociety,
  patchSociety,
  societyQueries,
} from '../../../api/societies'
import { useSelectedSocietyId } from '../../../hooks/useSelectedSocietyContext'
import { NSocieties } from '../../../interfaces/services/societies.interfaces'
import { i18nSocietyType } from '../../../i18n'

interface CreateSocietyFormProps {
  societyTypeDisplayName: SocietyTypes
  user: IUserModel
}

export const CreateSocietyForm = ({
  societyTypeDisplayName,
  user,
}: CreateSocietyFormProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { getErrorMessage } = useFormErrorMessage()
  const { setToastNotification } = useToastNotifications()
  const { setCurrentSocietyId } = useSelectedSocietyId()
  const navigate = useNavigate()

  const societyIsRealStateDeveloper =
    societyTypeDisplayName === SocietyTypes.HOUSING_COOPERATIVE_CONSTRUCTION
  const societyIsNeighbourhood =
    societyTypeDisplayName === SocietyTypes.NEIGHBOURHOOD

  const getDefaultValues = (): InferType<typeof societySchema> => {
    return {
      organisationNumber: '',
      companyOrganisationNumber: '',
      status: 'inactive',
      name: '',
      companyName: '',
      addressCountryCode: 'SE',
      addressCity: '',
      addressZip: '',
      addressStreet: '',
      billingEmail: user.email as string,
      billingUnits: 0,
      societyTypeDisplayName,
      typeOfSociety: 'legal_entity',
      masterAdminUserId: user._id,
    }
  }

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(societySchema),
    defaultValues: getDefaultValues(),
  })

  // Had to add this useEffect because useForm doesn't want to recompute its values when the type updates
  useEffect(() => {
    setValue('societyTypeDisplayName', societyTypeDisplayName)
  }, [setValue, societyTypeDisplayName])

  const showErrorMessage = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate('createSocietyForm.flashMessage.createSocietyFailure')
    )
  }

  const showSuccessMessage = (): void => {
    setToastNotification(
      ToastType.SUCCESS,
      translate('createSocietyForm.flashMessage.createSocietySuccess')
    )
  }

  const watchOrganisationNumber = watch('organisationNumber')

  const trimmedOrganisationNumber = watchOrganisationNumber?.replace('-', '')
  const { data: societiesByOrgNumber, isLoading: isSearchingSociety } =
    useQuery(
      societyQueries.getSocietiesByOrganisationNumber(trimmedOrganisationNumber)
    )

  const alreadyExistingSociety =
    societiesByOrgNumber && societiesByOrgNumber.length > 0

  const navigateToSociety = (societyId: string): void => {
    setCurrentSocietyId(societyId)
    navigate(reverseUrl('society'), { replace: true })
  }

  const navigateToSocietyConfirmActivation = (societyId: string): void => {
    setCurrentSocietyId(societyId)
    navigate(
      reverseUrl('find-society:confirm-activation-detail', {
        id: societyId,
      }),
      { replace: true, state: { navigatingFromSocietyCreated: true } }
    )
  }

  const { mutate: mutateActivateSociety, isPending: isActivatingSociety } =
    useMutation({
      mutationFn: async ({
        data,
      }: {
        data: NSocieties.NCreate.IRequestBody
      }) => {
        const society = (await createSociety(data)).data.data
        if (!society) throw new Error('Create Society error')
        await activateSociety(society._id)
        await patchSociety({
          id: society._id,
          body: {
            billingUnits: data.billingUnits,
          },
        })
        return society
      },
      onSuccess: (society) => {
        showSuccessMessage()
        if (societyIsRealStateDeveloper || societyIsNeighbourhood) {
          navigateToSociety(society._id)
        } else {
          navigateToSocietyConfirmActivation(society._id)
        }
      },
      onError: () => {
        showErrorMessage()
      },
    })

  const onSubmit = async (
    data: InferType<typeof societySchema>
  ): Promise<void> => {
    if (alreadyExistingSociety) return
    const body = data as NSocieties.NCreate.IRequestBody
    mutateActivateSociety({ data: body })
  }

  const onError = (): void => {
    setToastNotification(
      ToastType.SUCCESS,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  const closeAlreadyExistingSocietyModal = (): void => {
    setValue('organisationNumber', '')
  }

  const onChangeHelper = (
    e: React.FormEvent<HTMLInputElement>,
    onChange: (...event: unknown[]) => void
  ): void => {
    const str = e.currentTarget.value
    if (str !== '') {
      try {
        const val = parseInt(str, 10)
        onChange(val)
      } catch (error) {
        captureException(error)
      }
    } else {
      onChange(0)
    }
  }

  const customOrganisatioNumberError =
    watchOrganisationNumber &&
    watchOrganisationNumber.replace('-', '') ===
      societiesByOrgNumber?.[0]?.organisationNumber?.replace('-', '')
      ? {
          message: translate(
            'createSocietyForm.errors.societyAlreadyActivated'
          ),
          ref: {
            name: 'organisationNumber',
          },
          type: 'matches',
        }
      : undefined

  const loading = isSearchingSociety || isActivatingSociety

  return (
    <>
      <form
        className="mb-8 space-y-6 overflow-y-auto"
        onSubmit={handleSubmit(onSubmit, onError)}
      >
        {societyIsRealStateDeveloper && (
          <HorizontalFormSection
            title={translate('createSocietyForm.headers.realEstateDeveloper')}
          >
            <div className="flex-0 space-y-4 md:flex-1">
              <Controller
                control={control}
                render={({ field: { onChange, onBlur, value, name } }) => (
                  <FormControl
                    label={translate('common.form.labels.name')}
                    error={
                      errors.companyName && getErrorMessage(errors.companyName)
                    }
                    name={name}
                  >
                    <TextInput
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                    />
                  </FormControl>
                )}
                name="companyName"
              />
              <Controller
                control={control}
                render={({ field: { onChange, onBlur, value, name } }) => {
                  const formattedValue = value
                    ? formatOrganisationNumber(value)
                    : ''
                  return (
                    <FormControl
                      label={translate('common.form.labels.organisationNumber')}
                      error={
                        errors.companyOrganisationNumber &&
                        getErrorMessage(errors.companyOrganisationNumber)
                      }
                      name={name}
                    >
                      <OrganisationNumberTextInput
                        value={formattedValue}
                        onChange={onChange}
                        onBlur={onBlur}
                      />
                    </FormControl>
                  )
                }}
                name="companyOrganisationNumber"
              />
            </div>
          </HorizontalFormSection>
        )}
        <HorizontalFormSection
          title={
            societyIsRealStateDeveloper
              ? translate('createSocietyForm.societyOrProjectTitle')
              : ''
          }
          hideTitleColumn={!societyIsRealStateDeveloper}
        >
          {!societyIsNeighbourhood && (
            <>
              <Controller
                control={control}
                render={({ field: { onChange, onBlur, value, name } }) => {
                  const formattedValue = value
                    ? formatOrganisationNumber(value)
                    : ''
                  return (
                    <FormControl
                      label={translate('common.form.labels.organisationNumber')}
                      error={
                        errors.organisationNumber
                          ? getErrorMessage(errors.organisationNumber)
                          : customOrganisatioNumberError &&
                            getErrorMessage(customOrganisatioNumberError)
                      }
                      name={name}
                    >
                      <OrganisationNumberTextInput
                        value={formattedValue}
                        onChange={onChange}
                        onBlur={() => {
                          if (onBlur) {
                            onBlur()
                          }
                        }}
                      />
                    </FormControl>
                  )
                }}
                name="organisationNumber"
              />
              <Controller
                control={control}
                render={({ field: { onChange, value, name } }) => (
                  <FormControl
                    label={translate(
                      'createSocietyForm.labels.numberOfBillingUnits',
                      { context: i18nSocietyType(societyTypeDisplayName) }
                    )}
                    error={
                      errors.billingUnits &&
                      getErrorMessage(errors.billingUnits)
                    }
                    name={name}
                  >
                    <TextInput
                      value={value ? `${value}` : ''}
                      onBlur={(e) => onChangeHelper(e, onChange)}
                      onChange={(e) => onChangeHelper(e, onChange)}
                      type="number"
                    />
                  </FormControl>
                )}
                name="billingUnits"
              />
            </>
          )}
          <Controller
            control={control}
            render={({ field: { onChange, onBlur, value, name } }) => (
              <FormControl
                label={translate('createSocietyForm.labels.societyName')}
                error={errors.name && getErrorMessage(errors.name)}
                name={name}
                infoTooltipTitle={translate(
                  'createSocietyForm.societyNameTooltipTitle'
                )}
                infoTooltipText={translate(
                  'createSocietyForm.societyNameTooltipText'
                )}
                toolTipUseFlexibleWidth
              >
                <TextInput value={value} onChange={onChange} onBlur={onBlur} />
              </FormControl>
            )}
            name="name"
          />
          <Controller
            control={control}
            render={({ field: { onChange, onBlur, value, name } }) => (
              <FormControl
                label={translate('createSocietyForm.addressStreet')}
                error={
                  errors.addressStreet && getErrorMessage(errors.addressStreet)
                }
                name={name}
              >
                <TextInput value={value} onChange={onChange} onBlur={onBlur} />
              </FormControl>
            )}
            name="addressStreet"
          />
          <Controller
            control={control}
            render={({ field: { onChange, onBlur, value, name } }) => (
              <FormControl
                label={translate('common.form.labels.addressZip')}
                error={errors.addressZip && getErrorMessage(errors.addressZip)}
                name={name}
              >
                <TextInput
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  type="number"
                />
              </FormControl>
            )}
            name="addressZip"
          />
          <Controller
            control={control}
            render={({ field: { onChange, onBlur, value, name } }) => (
              <FormControl
                label={translate('common.form.labels.addressCity')}
                error={
                  errors.addressCity && getErrorMessage(errors.addressCity)
                }
                name={name}
              >
                <TextInput value={value} onChange={onChange} onBlur={onBlur} />
              </FormControl>
            )}
            name="addressCity"
          />
          <div className="my-4 flex w-full justify-end">
            <Button
              variant={ButtonVariant.PRIMARY}
              loading={loading}
              disabled={!isValid || loading}
              label={translate('createSocietyForm.createSocietyTitle')}
              type="submit"
            />
          </div>
        </HorizontalFormSection>
      </form>
      <AlreadyExistingSocietyModal
        show={!!alreadyExistingSociety}
        close={closeAlreadyExistingSocietyModal}
        society={societiesByOrgNumber?.[0]}
      />
    </>
  )
}
